import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    // 1. Find the row with strict type checking
    /** @type {any} */
    const lineItemRow = this.refs.cartItemRows.find((row) => {
      const rowIndex = row instanceof HTMLElement ? row.dataset.index : null;
      return rowIndex && parseInt(rowIndex) === line;
    }) || this.querySelector(`[data-index="${line}"]`);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // 2. Guard the dataset access
    /** @type {string | undefined} */
    let bundleId;
    if (lineItemRow && lineItemRow instanceof HTMLElement) {
      bundleId = lineItemRow.dataset.bundleId;
    }

    if (bundleId) {
      this.updateBundleQuantity(bundleId, quantity, 'change');
    } else {
      this.updateQuantity({
        line,
        quantity,
        action: 'change',
      });
    }

    if (!lineItemRow) return;

    const textComponent = /** @type {any} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    /** @type {any} */
    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => {
      const rowIndex = row instanceof HTMLElement ? row.dataset.index : null;
      return rowIndex && parseInt(rowIndex) === line;
    }) || this.querySelector(`[data-index="${line}"]`);
    
    /** @type {string | undefined} */
    let bundleId;
    if (cartItemRowToRemove && cartItemRowToRemove instanceof HTMLElement) {
      bundleId = cartItemRowToRemove.dataset.bundleId;
    }

    if (bundleId) {
      this.updateBundleQuantity(bundleId, 0, 'clear');
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    // Animation logic
    const rowsToRemove = [cartItemRowToRemove];
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Synchronized update for all items in a bundle
   * @param {string} bundleId
   * @param {number} quantity
   * @param {string} action
   */
  async updateBundleQuantity(bundleId, quantity, action) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#disableCartItems();
    
    /** @type {TextComponent | null} */
    const cartTotal = this.refs.cartTotal;
    cartTotal?.shimmer();

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      /** @type {Record<string, number>} */
      const updates = {};

      cart.items.forEach((/** @type {any} */ item) => {
        if (item.properties && item.properties._bundle_id === bundleId) {
          updates[item.key] = quantity;
        }
      });

      const sectionsToUpdate = this.#getSectionsToUpdate();

      const updateResponse = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        })
      }));

      const parsedResponse = await updateResponse.json();
      this.#processCartUpdate(parsedResponse);

    } catch (error) {
      console.error('Bundle Update Error:', error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates the quantity for standard items.
   * @param {any} config
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    this.refs.cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponse = JSON.parse(responseText);
        if (parsedResponse.errors) {
          this.#handleCartError(line, parsedResponse);
          return;
        }
        this.#processCartUpdate(parsedResponse);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Shared processing for cart update responses
   * @param {any} parsedResponse
   */
  #processCartUpdate(parsedResponse) {
    resetShimmer(this);

    const newSectionHTML = new DOMParser().parseFromString(
      parsedResponse.sections[this.sectionId],
      'text/html'
    );

    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponse);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponse, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponse.sections,
      })
    );

    morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Gets sections to update
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const sections = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });
    return sections;
  }

/**
   * Handles the cart error.
   * @param {number} line
   * @param {any} parsedResponseText
   */
#handleCartError = (line, parsedResponseText) => {
  const quantitySelector = this.refs.quantitySelectors[line - 1];
  
  // We cast this to any to allow .value and .defaultValue access without linter complaints
  /** @type {any} */
  const quantityInput = quantitySelector?.querySelector('input');
  
  if (!quantityInput) return;
  
  quantityInput.value = quantityInput.defaultValue;

  const cartItemError = this.refs[`cartItemError-${line}`];
  
  // Handle potential array or undefined from the refs system
  const rawErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
  
  /** @type {any} */
  const cartItemErrorContainer = Array.isArray(rawErrorContainer) ? rawErrorContainer[0] : rawErrorContainer;

  if (cartItemError instanceof HTMLElement) {
    cartItemError.textContent = parsedResponseText.errors;
  }

  // The '&&' check ensures we don't call classList on null or undefined
  if (cartItemErrorContainer && cartItemErrorContainer instanceof HTMLElement) {
    cartItemErrorContainer.classList.remove('hidden');
  }
};

  /**
   * Handles the discount update.
   * @param {any} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   * @param {any} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() { this.classList.add('cart-items-disabled'); }
  #enableCartItems() { this.classList.remove('cart-items-disabled'); }

  /**
   * Updates quantity selectors
   * @param {any} updatedCart
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (input) input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector) {
            /** @type {any} */ (selector).updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}